import React, { useState, useEffect, ReactElement } from "react";
import { Toaster, toast } from "react-hot-toast";
import { GetUserTasks } from "../Services/APIService";
import { useNavigate } from "react-router-dom";
import UpdatedUserTaskComponent from "./UpdatedUserTaskComponent";

interface Task {
  id: number;
  leadId: number;
  projectId: number;
  assignedUser: number;
  followupTypeId: number;
  followupDate: string;
  taskNotes: string;
  isPostponed: boolean;
  postponeReason: string | null;
  rescheduleDate: string | null;
  reminderDate: string;
  isCompleted: boolean;
  isCanceled: boolean;
  taskCompletionDate: string | null;
  createdBy: number;
  createdByName: string | null;
  createdOn: string;
  modifiedBy: number | null;
  modifiedOn: string;
  modifiedByName: string | null;
  projectName: string;
  assignedUserName: string;
  followupTypeName: string;
  leadName: string;
}

function getCurrentISTDateTimeISO() {
  const now = new Date();
  const offsetInMinutes = 330;
  const localTime = new Date(now.getTime() + offsetInMinutes * 60 * 1000);
  const yyyy = localTime.getUTCFullYear();
  const mm = String(localTime.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(localTime.getUTCDate()).padStart(2, "0");
  const hh = String(localTime.getUTCHours()).padStart(2, "0");
  const mi = String(localTime.getUTCMinutes()).padStart(2, "0");
  const ss = String(localTime.getUTCSeconds()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}:${ss}+05:30`;
}

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

  .zt-root {
    font-family: 'Plus Jakarta Sans', sans-serif;
    background: #fff;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 2px 20px rgba(108,92,231,0.10), 0 1px 4px rgba(0,0,0,0.05);
    border: 1px solid rgba(108,92,231,0.09);
  }

  /* ── Header ── */
  .zt-header {
    background: linear-gradient(120deg, #a68490 0%, #7c5cbf 50%, #846e96 100%);
    padding: 14px 16px 0;
  }
  .zt-title {
    font-size: 0.72rem; font-weight: 800;
    letter-spacing: 0.15em; text-transform: uppercase;
    color: #fff; margin: 0 0 12px;
    display: flex; align-items: center; gap: 8px;
  }
  .zt-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: #fff; flex-shrink: 0;
    box-shadow: 0 0 0 3px rgba(255,255,255,0.22);
    animation: zt-pulse 2.4s ease-in-out infinite;
  }
  @keyframes zt-pulse {
    0%,100% { box-shadow: 0 0 0 3px rgba(255,255,255,0.22); }
    50%      { box-shadow: 0 0 0 6px rgba(255,255,255,0.06); }
  }

  /* ── Tab nav ── */
  .zt-nav { display: flex; list-style: none; margin: 0; padding: 0; gap: 2px; }
  .zt-nav-item { flex: 1; }
  .zt-nav-btn {
    width: 100%; border: none;
    background: rgba(255,255,255,0.10);
    border-radius: 8px 8px 0 0;
    padding: 7px 4px 8px;
    cursor: pointer;
    color: rgba(255,255,255,0.6);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 0.58rem; font-weight: 700;
    letter-spacing: 0.08em; text-transform: uppercase;
    display: flex; flex-direction: column; align-items: center; gap: 3px;
    position: relative; transition: all 0.2s ease;
  }
  .zt-nav-btn:hover { background: rgba(255,255,255,0.18); color: #fff; }
  .zt-nav-btn.zt-active { background: #fff; color: #5b4dd6; }
  .zt-nav-btn .zt-bar {
    position: absolute; bottom: 0; left: 18%; right: 18%;
    height: 2px; border-radius: 2px 2px 0 0; background: #5b4dd6;
    transform: scaleX(0); transition: transform 0.25s cubic-bezier(.23,1,.32,1);
  }
  .zt-nav-btn.zt-active .zt-bar { transform: scaleX(1); }
  .zt-badge {
    font-size: 0.56rem; font-weight: 700;
    padding: 1px 6px; border-radius: 20px; line-height: 1.5;
    background: rgba(255,255,255,0.18); color: rgba(255,255,255,0.85);
    transition: all 0.2s;
  }
  .zt-nav-btn.zt-active .zt-badge { background: rgba(91,77,214,0.12); color: #5b4dd6; }
  .zt-badge.alert { background: rgba(239,68,68,0.28); color: #fecaca; }
  .zt-nav-btn.zt-active .zt-badge.alert { background: rgba(239,68,68,0.12); color: #ef4444; }
  /* Site visit badge — teal accent */
  .zt-badge.sitevisit { background: rgba(20,184,166,0.28); color: #ccfbf1; }
  .zt-nav-btn.zt-active .zt-badge.sitevisit { background: rgba(20,184,166,0.13); color: #0d9488; }

  /* ── Body ── */
  .zt-body {
    background: #f5f3ff;
    padding: 8px 8px 6px;
    min-height: 385px; max-height: 385px;
    overflow-y: auto;
  }
  .zt-body::-webkit-scrollbar { width: 3px; }
  .zt-body::-webkit-scrollbar-thumb { background: rgba(91,77,214,0.2); border-radius: 4px; }

  /* ── Card ── */
  .zt-card {
    background: #fff;
    border-radius: 9px;
    margin-bottom: 5px;
    cursor: pointer;
    border-left: 3px solid #c4b5fd;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
    transition: all 0.15s ease;
  }
  .zt-card:hover {
    border-left-color: #5b4dd6;
    box-shadow: 0 3px 10px rgba(91,77,214,0.12);
    transform: translateY(-1px);
  }
  .v-missed        { border-left-color: #fca5a5; }
  .v-missed:hover  { border-left-color: #ef4444; }
  .v-overdue       { border-left-color: #fcd34d; }
  .v-overdue:hover { border-left-color: #f59e0b; }
  .v-done          { border-left-color: #86efac; }

  /* Site visit card — teal left border */
  .v-sitevisit       { border-left-color: #5eead4; }
  .v-sitevisit:hover {
    border-left-color: #0d9488;
    box-shadow: 0 3px 10px rgba(13,148,136,0.14);
  }

  .zt-card-inner {
    padding: 7px 9px 7px 9px;
    display: flex; gap: 8px; align-items: flex-start;
  }

  /* Icon */
  .zt-card-icon {
    width: 30px; height: 30px; border-radius: 7px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: 0.78rem; margin-top: 1px;
    background: rgba(91,77,214,0.08); color: #5b4dd6;
  }
  .v-missed    .zt-card-icon { background: rgba(239,68,68,0.08);   color: #ef4444; }
  .v-overdue   .zt-card-icon { background: rgba(245,158,11,0.10);  color: #f59e0b; }
  .v-done      .zt-card-icon { background: rgba(34,197,94,0.08);   color: #22c55e; }
  .v-sitevisit .zt-card-icon { background: rgba(20,184,166,0.10);  color: #0d9488; }

  .zt-card-info { flex: 1; min-width: 0; }

  /* Row 1 */
  .zt-card-row1 { display: flex; align-items: center; gap: 5px; margin-bottom: 3px; }
  .zt-lead-name {
    font-size: 0.76rem; font-weight: 700; color: #1e1b4b;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    max-width: 120px; flex-shrink: 0;
  }
  .v-missed    .zt-lead-name { color: #dc2626; }
  .v-overdue   .zt-lead-name { color: #b45309; }
  .v-sitevisit .zt-lead-name { color: #0f766e; }

  .zt-type-chip {
    font-size: 0.52rem; font-weight: 700;
    letter-spacing: 0.05em; text-transform: uppercase;
    padding: 1px 5px; border-radius: 20px;
    background: rgba(91,77,214,0.09); color: #5b4dd6; flex-shrink: 0;
  }
  .v-missed    .zt-type-chip { background: rgba(239,68,68,0.09);   color: #ef4444; }
  .v-overdue   .zt-type-chip { background: rgba(245,158,11,0.10);  color: #b45309; }
  .v-done      .zt-type-chip { background: rgba(34,197,94,0.09);   color: #16a34a; }
  .v-sitevisit .zt-type-chip { background: rgba(20,184,166,0.10);  color: #0d9488; }

  .zt-ago {
    margin-left: auto; flex-shrink: 0;
    font-size: 0.52rem; font-weight: 700;
    padding: 1px 5px; border-radius: 20px;
    background: rgba(239,68,68,0.10); color: #ef4444;
  }

  /* "upcoming" label on site-visit tab */
  .zt-upcoming-tag {
    margin-left: auto; flex-shrink: 0;
    font-size: 0.52rem; font-weight: 700;
    padding: 1px 6px; border-radius: 20px;
    background: rgba(20,184,166,0.12); color: #0d9488;
  }

  /* Row 2 */
  .zt-meta-row {
    display: flex; align-items: center; gap: 8px;
    margin-bottom: 4px; flex-wrap: nowrap; overflow: hidden;
  }
  .zt-meta-item {
    display: flex; align-items: center; gap: 3px;
    font-size: 0.62rem; font-weight: 500; color: #6b7280;
    white-space: nowrap; flex-shrink: 0;
  }
  .zt-meta-item i { font-size: 0.58rem; color: #9ca3af; }
  .v-missed    .zt-pill-time { color: #dc2626; }
  .v-missed    .zt-pill-time i { color: #ef4444; }
  .v-overdue   .zt-pill-time { color: #b45309; }
  .v-overdue   .zt-pill-time i { color: #f59e0b; }
  .v-sitevisit .zt-pill-time { color: #0d9488; }
  .v-sitevisit .zt-pill-time i { color: #14b8a6; }

  /* Row 3 */
  .zt-assignee-row { display: flex; align-items: center; gap: 5px; }
  .zt-avatar {
    width: 17px; height: 17px; border-radius: 50%;
    background: linear-gradient(135deg, #5b4dd6, #9b6fd4);
    display: flex; align-items: center; justify-content: center;
    font-size: 0.44rem; font-weight: 700; color: #fff; flex-shrink: 0;
  }
  .v-sitevisit .zt-avatar { background: linear-gradient(135deg, #0d9488, #14b8a6); }
  .zt-assignee-name { font-size: 0.62rem; font-weight: 500; color: #6b7280; }

  /* Notes snippet — site visit tab */
  .zt-notes-snippet {
    font-size: 0.60rem; color: #9ca3af; margin-top: 3px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    max-width: 100%;
  }

  /* Arrow */
  .zt-card-arrow {
    color: rgba(91,77,214,0.18); font-size: 0.58rem;
    align-self: center; flex-shrink: 0; transition: all 0.15s;
  }
  .zt-card:hover .zt-card-arrow { color: #5b4dd6; transform: translateX(2px); }
  .v-sitevisit .zt-card-arrow { color: rgba(13,148,136,0.18); }
  .v-sitevisit:hover .zt-card-arrow { color: #0d9488; }

  /* ── Empty ── */
  .zt-empty {
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; padding: 44px 20px; text-align: center;
  }
  .zt-empty-icon {
    width: 42px; height: 42px; border-radius: 12px;
    background: rgba(91,77,214,0.07);
    display: flex; align-items: center; justify-content: center;
    font-size: 1.1rem; color: #a78bfa; margin-bottom: 10px;
  }
  .zt-empty-title { font-weight: 700; font-size: 0.76rem; color: #9ca3af; margin: 0 0 3px; }
  .zt-empty-sub   { font-size: 0.66rem; color: #c4c4d4; margin: 0; }

  /* ── Shimmer ── */
  .zt-shimmer-wrap { display: flex; flex-direction: column; gap: 5px; }
  .zt-shimmer {
    height: 68px; border-radius: 9px;
    background: linear-gradient(90deg,
      rgba(91,77,214,0.05) 25%, rgba(91,77,214,0.11) 50%, rgba(91,77,214,0.05) 75%);
    background-size: 200% 100%; animation: zt-shim 1.6s infinite;
  }
  @keyframes zt-shim {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  .zt-error {
    display: flex; align-items: center; gap: 10px;
    padding: 12px 16px; margin: 12px;
    background: #fff5f5; border: 1px solid rgba(239,68,68,0.2);
    border-radius: 8px; color: #ef4444; font-size: 0.74rem;
  }
`;

// Detect site visit by name so we don't rely on a hardcoded ID
const isSiteVisit = (task: Task): boolean =>
  task.followupTypeName?.toLowerCase().includes("visit") ||
  task.followupTypeName?.toLowerCase().includes("site");

const DashboardTaskTabs: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>("today");
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState<boolean>(false);
  const [updateStatusData, setUpdateStatusData] = useState<{
    tenantId: number; leadId: number; assignedUser: number;
    isCompleted: boolean; followupDate: string; actionType: string;
  } | null>(null);

  const now = getCurrentISTDateTimeISO();

  const fetchTasks = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const data = await GetUserTasks();
      // Keep all tasks (site visits included); only exclude followupTypeId 1
      setTasks(data.filter((t) => t.followupTypeId !== 1));
    } catch (err) {
      setError("Error fetching tasks. Please try again later.");
      toast.error("Error fetching tasks");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchTasks(); }, []);

  const redirectToUserTaskEdit = (taskId: number): void => {
    const t = tasks.find((x) => x.id === taskId);
    if (t) {
      navigate("/LeadDashboard", { state: { leadId: t.leadId } });
    }
  };

  const handleModalClose = async (): Promise<void> => {
    setIsUpdateStatusModalOpen(false);
    setUpdateStatusData(null);
    await fetchTasks();
  };

  const getBounds = () => {
    const current = new Date(now);
    const s0 = new Date(current); s0.setHours(0, 0, 0, 0);
    const e0 = new Date(current); e0.setHours(23, 59, 59, 999);
    const s1 = new Date(s0); s1.setDate(s1.getDate() + 1);
    const e1 = new Date(e0); e1.setDate(e1.getDate() + 1);
    return { current, s0, e0, s1, e1 };
  };

  const getTomorrow  = (): Task[] => {
    const { s1, e1 } = getBounds();
    return tasks.filter((t) => {
      const d = new Date(t.followupDate);
      return d >= s1 && d <= e1 && !t.isCanceled && !t.isCompleted && !t.isPostponed
        && !isSiteVisit(t);
    });
  };

  const getToday = (): Task[] => {
    const { s0, e0 } = getBounds();
    return tasks.filter((t) => {
      const d = new Date(t.followupDate);
      return d >= s0 && d <= e0 && !t.isCanceled && !t.isPostponed && !t.isCompleted
        && !isSiteVisit(t);
    });
  };

  const getMissed = (): Task[] => {
    const { current } = getBounds();
    return tasks.filter((t) => {
      const d = new Date(t.followupDate);
      return d < current && !t.isCanceled && !t.isCompleted && !t.isPostponed
        && !isSiteVisit(t);
    });
  };

  // Site Visit tab — ALL site visits (missed + upcoming), not cancelled, not completed
  // Sorted: missed (past) first by most recent, then upcoming by nearest
  const getSiteVisits = (): Task[] => {
    const { s0 } = getBounds();
    const missed   = tasks.filter((t) => isSiteVisit(t) && !t.isCanceled && !t.isCompleted && new Date(t.followupDate) < s0)
                          .sort((a, b) => new Date(b.followupDate).getTime() - new Date(a.followupDate).getTime());
    const upcoming = tasks.filter((t) => isSiteVisit(t) && !t.isCanceled && !t.isCompleted && new Date(t.followupDate) >= s0)
                          .sort((a, b) => new Date(a.followupDate).getTime() - new Date(b.followupDate).getTime());
    return [...upcoming, ...missed];
  };

  const iconClass = (name: string): string => {
    const n = name.toLowerCase();
    if (n.includes("call"))     return "bi-telephone-fill";
    if (n.includes("whatsapp")) return "bi-whatsapp";
    if (n.includes("email"))    return "bi-envelope-fill";
    if (n.includes("meeting"))  return "bi-people-fill";
    if (n.includes("sms"))      return "bi-chat-dots-fill";
    if (n.includes("visit"))    return "bi-geo-alt-fill";
    if (n.includes("note"))     return "bi-journal-text";
    return "bi-bell-fill";
  };

  const initials = (name: string): string =>
    name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();

  // Format a relative date label for site visits: "Today", "Tomorrow", "Wed, Apr 03"
  const siteVisitDayLabel = (followupDate: string): string => {
    const { s0, e0, s1, e1 } = getBounds();
    const d = new Date(followupDate);
    if (d >= s0 && d <= e0) return "Today";
    if (d >= s1 && d <= e1) return "Tomorrow";
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "2-digit" });
  };

  const renderList = (list: Task[], tab: string): ReactElement => {
    const { current } = getBounds();

    if (!list.length) {
      const emptyMeta: Record<string, { icon: string; title: string; sub: string }> = {
        today:     { icon: "bi-check2-circle", title: "All clear for today",       sub: "No follow-ups scheduled today" },
        tomorrow:  { icon: "bi-calendar-check", title: "Nothing tomorrow",         sub: "No follow-ups scheduled for tomorrow" },
        missed:    { icon: "bi-clock-history",  title: "No missed tasks",          sub: "You're all caught up!" },
        sitevisit: { icon: "bi-geo-alt",        title: "No upcoming site visits",  sub: "No site visits scheduled" },
      };
      const em = emptyMeta[tab] ?? { icon: "bi-inbox", title: "All clear", sub: "No tasks in this category" };
      return (
        <div className="zt-empty">
          <div className="zt-empty-icon"><i className={`bi ${em.icon}`} /></div>
          <p className="zt-empty-title">{em.title}</p>
          <p className="zt-empty-sub">{em.sub}</p>
        </div>
      );
    }

    return (
      <div>
        {list.map((task) => {
          const d = new Date(task.followupDate);
          const isOverdueToday = tab === "today" && d < current && !task.isCompleted && !task.isCanceled && !task.isPostponed;

          let variant = "";
          if (tab === "missed")                                      variant = "v-missed";
          else if (tab === "sitevisit" && d < current)              variant = "v-missed";  // overdue site visit → red
          else if (tab === "sitevisit")                             variant = "v-sitevisit";
          else if (isOverdueToday)                                   variant = "v-overdue";
          else if (task.isCompleted)                                 variant = "v-done";

          const timeStr = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
          const dateStr = d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "2-digit", year: "numeric" });
          const assignee = task.assignedUserName || "Admin";

          let agoLabel = "";
          if (tab === "missed" || (tab === "sitevisit" && d < current)) {
            const diffMs = current.getTime() - d.getTime();
            const diffH  = Math.floor(diffMs / 3600000);
            const diffD  = Math.floor(diffH / 24);
            agoLabel = diffD >= 1 ? `${diffD}d ago` : `${diffH}h ago`;
          }

          return (
            <div
              key={task.id}
              className={`zt-card ${variant}`}
              onClick={() => redirectToUserTaskEdit(task.id)}
            >
              <div className="zt-card-inner">
                <div className="zt-card-icon">
                  <i className={`bi ${iconClass(task.followupTypeName)}`} />
                </div>

                <div className="zt-card-info">
                  {/* Row 1: lead + chip + ago / upcoming */}
                  <div className="zt-card-row1">
                    <span className="zt-lead-name">{task.leadName}</span>
                    <span className="zt-type-chip">{task.followupTypeName}</span>
                    {agoLabel && <span className="zt-ago">{agoLabel}</span>}
                    {tab === "sitevisit" && d >= current && (
                      <span className="zt-upcoming-tag">{siteVisitDayLabel(task.followupDate)}</span>
                    )}
                  </div>

                  {/* Row 2: time · date · project */}
                  <div className="zt-meta-row">
                    <span className="zt-meta-item zt-pill-time">
                      <i className="bi bi-clock-fill" />{timeStr}
                    </span>
                    <span className="zt-meta-item">
                      <i className="bi bi-calendar3" />{dateStr}
                    </span>
                    {task.projectName && (
                      <span className="zt-meta-item">
                        <i className="bi bi-building" />{task.projectName}
                      </span>
                    )}
                  </div>

                  {/* Row 3: assignee + optional notes for site visit */}
                  <div className="zt-assignee-row">
                    <div className="zt-avatar">{initials(assignee)}</div>
                    <span className="zt-assignee-name">{assignee}</span>
                  </div>
                  {tab === "sitevisit" && task.taskNotes && (
                    <div className="zt-notes-snippet">
                      <i className="bi bi-chat-left-text" style={{ marginRight: 3 }} />
                      {task.taskNotes}
                    </div>
                  )}
                </div>

                <span className="zt-card-arrow"><i className="bi bi-chevron-right" /></span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Tab order: Tomorrow · Today · Missed · Site Visit  (Cancelled removed)
  const tabs = ["tomorrow", "today", "missed", "sitevisit"];

  const meta: Record<string, { label: string; count: number; badgeType?: string }> = {
    tomorrow:  { label: "Tomorrow",   count: getTomorrow().length  },
    today:     { label: "Today",      count: getToday().length     },
    missed:    { label: "Missed",     count: getMissed().length,    badgeType: getMissed().length > 0 ? "alert" : "" },
    sitevisit: { label: "Site Visit", count: getSiteVisits().length, badgeType: getSiteVisits().length > 0 ? "sitevisit" : "" },
  };

  const content: Record<string, () => ReactElement> = {
    tomorrow:  () => renderList(getTomorrow(),  "tomorrow"),
    today:     () => renderList(getToday(),     "today"),
    missed:    () => renderList(getMissed(),    "missed"),
    sitevisit: () => renderList(getSiteVisits(), "sitevisit"),
  };

  return (
    <>
      <style>{styles}</style>
      <Toaster toastOptions={{ style: { fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: "0.8rem", borderRadius: "10px" } }} />

      <div className="zt-root">
        <div className="zt-header">
          <h5 className="zt-title"><span className="zt-dot" />Follow-Up</h5>
          <ul className="zt-nav">
            {tabs.map((tab) => {
              const { label, count, badgeType } = meta[tab];
              return (
                <li className="zt-nav-item" key={tab}>
                  <button
                    className={`zt-nav-btn ${activeTab === tab ? "zt-active" : ""}`}
                    onClick={() => setActiveTab(tab)}
                    type="button"
                  >
                    {label}
                    <span className={`zt-badge ${badgeType ?? ""}`}>{count}</span>
                    <span className="zt-bar" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="zt-body">
          {isLoading ? (
            <div className="zt-shimmer-wrap">
              {[1, 2, 3, 4].map((i) => <div key={i} className="zt-shimmer" />)}
            </div>
          ) : error ? (
            <div className="zt-error"><i className="bi bi-exclamation-circle-fill" /> {error}</div>
          ) : (
            content[activeTab]?.()
          )}
        </div>
      </div>

      {updateStatusData && (
        <UpdatedUserTaskComponent
          tenantId={updateStatusData.tenantId}
          leadId={updateStatusData.leadId}
          assignedUser={updateStatusData.assignedUser}
          isCompleted={updateStatusData.isCompleted}
          followupDate={updateStatusData.followupDate}
          actionType={updateStatusData.actionType}
          isOpen={isUpdateStatusModalOpen}
          onClose={handleModalClose}
        />
      )}
    </>
  );
};

export default DashboardTaskTabs;